import { Box, Typography } from '@mui/material';
import RadioGroup from 'components/common/RadioGroup';
import type { FC } from 'react';
import FeaturedPlayListOutlinedIcon from '@mui/icons-material/FeaturedPlayListOutlined';
import { useRuleGroups } from '../api';
import { BasicSummary, LabelFormSection } from '../../components';
import Input from 'components/common/Input';
import Select from 'components/common/Select';
import { ProjectSelect } from 'features/projects';
import { useFormContext } from 'react-hook-form';
import ChipInput from 'components/common/ChipInput';
import type { BasicFormValues } from '../types';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getReferencesOptions } from '../../api';
import NameRuleChangedMessage from '../../components/NameRuleChangedMessage';
import BasicFieldSkeleton from '../../components/BasicFieldSkeleton';
import { useCurrentNetwork } from '../../../../hooks';
import useRulePageMode from '../../hooks/useRulePageMode';
import type { RuleEntry } from 'features/rules';
import { NotificationLevelSelect, NotificationMessageFields, NotificationChannelsAutocomplete } from 'features/rules';

const RuleStatus = () => {
  const { isRead } = useRulePageMode();
  return (
    <Box sx={{ display: 'flex', gap: 3 }}>
      <Typography variant="subtitle1" sx={{ lineHeight: 2.5 }}>
        Status
      </Typography>
      <RadioGroup
        row
        name="ruleStatus"
        options={[
          { value: 'active', label: 'Active', disabled: isRead },
          { value: 'paused', label: 'Paused', disabled: isRead },
          { value: 'archived', label: 'Archived', disabled: isRead },
        ]}
      />
    </Box>
  );
};

const LevelSelect = () => {
  const network = useCurrentNetwork();
  const { data: levels } = useSuspenseQuery({
    ...getReferencesOptions(network),
    select: (data) => data.levels.map(({ name, id }) => ({ value: id, label: name })),
  });

  return (
    <Select
      name="level"
      inputLabel="level"
      required
      selectAll={false}
      options={levels}
      // disable because change of level implies active_ads_count and active_adsets_count metrics
      disabled
    />
  );
};

const RuleGroupSelect = () => {
  const { isRead } = useRulePageMode();
  const { data: ruleGroups } = useRuleGroups({
    config: {
      select: (data) => data.map(({ name, id }) => ({ value: id, label: name })),
    },
  });
  return <Select name="groupId" inputLabel="rule group" selectAll={false} options={ruleGroups} disabled={isRead} />;
};

const Description = () => {
  const { isRead } = useRulePageMode();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle1">Description</Typography>
      <Box sx={{ py: 1.5, borderRadius: 1.5 }}>
        <Input id="description" name="description" multiline disabled={isRead} />
      </Box>
    </Box>
  );
};

const RuleEditBasicForm: FC<{ rule: RuleEntry }> = ({ rule }) => {
  const { getFieldState, getValues } = useFormContext<BasicFormValues>();
  const { isRead, isEdit } = useRulePageMode();
  const isNameDirty = getFieldState('name').isDirty && isEdit;

  console.log(getValues());

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          Rule settings
        </Typography>
        <RuleStatus />
      </Box>
      <Box
        sx={(t) => ({
          display: 'flex',
          gap: 2,
          width: '100%',
          flexDirection: 'column',
          border: `solid ${`rgba(0, 8, 46, 0.12)`}`,
          borderRadius: t.borderRadius.md,
          p: 2,
        })}
      >
        <Box>
          <LabelFormSection label="Basic" icon={<FeaturedPlayListOutlinedIcon />} />
        </Box>
        <Box sx={{ display: 'flex', width: '100%', gap: 4 }}>
          <Box sx={{ display: 'flex', width: 'calc(60% - 50px)', gap: 3, flexDirection: 'column' }}>
            <Input name="name" label="name" innerLabel disabled={isRead} />
            {isNameDirty && <NameRuleChangedMessage />}
            <BasicFieldSkeleton>
              <ProjectSelect name="project" inputLabel="project" required disabled={isRead} />
            </BasicFieldSkeleton>
            <BasicFieldSkeleton>
              <LevelSelect />
            </BasicFieldSkeleton>
            <ChipInput name="facebookAdAccountIds" inputLabel="facebook_ad_account_ids" disabled={isRead} />
            <BasicFieldSkeleton>
              <RuleGroupSelect />
            </BasicFieldSkeleton>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Typography variant="subtitle2">Notifications</Typography>
              <BasicFieldSkeleton>
                <NotificationChannelsAutocomplete name="notificationChannels" inputLabel="name" />
              </BasicFieldSkeleton>
              <BasicFieldSkeleton>
                <NotificationLevelSelect />
              </BasicFieldSkeleton>
              <NotificationMessageFields ruleId={rule.id} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flex: 1, gap: 3, flexDirection: 'column' }}>
            <BasicSummary team={rule.team} flows={rule.flows} actionTypes={rule.action_types} />
            <Description />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RuleEditBasicForm;
